/*
 * Tie Benon — storefront behaviour (tb-theme.js)
 * Built from src/js/*.js by tools/build.py. Plain JavaScript, no dependencies.
 * Components are custom elements or delegated listeners, so they keep working
 * when the theme editor re-renders a section.
 */
(() => {
  'use strict';
  if (window.TB && window.TB.ready) return;
  const TB = (window.TB = window.TB || {});
  TB.ready = true;
  TB.routes = Object.assign(
    { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', search: '/search', predictive: '/search/suggest', recommendations: '/recommendations/products' },
    TB.routes || {},
  );

  /* ---------------------------------------------------------------- utils */

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };
  const root = (path) => {
    const base = TB.routes.root || '/';
    return (base.endsWith('/') ? base.slice(0, -1) : base) + path;
  };

  /**
   * Heritage scrolls .page-wrapper instead of the window on screens 990px and
   * wider; these helpers always use whichever element is scrolling the page.
   */
  const SQUEEZE = window.matchMedia('(min-width: 990px)');
  const scroller = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (SQUEEZE.matches && wrapper && getComputedStyle(wrapper).overflowY !== 'visible') return wrapper;
    return document.scrollingElement || document.documentElement;
  };
  const scrollTop = () => scroller().scrollTop;
  const onScroll = (fn) => {
    window.addEventListener('scroll', fn, { passive: true });
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', fn, { passive: true });
  };

  /* Screen-reader announcements */
  const announce = (text) => {
    const el = document.getElementById('tb-announcer');
    if (!el) return;
    el.textContent = '';
    setTimeout(() => {
      el.textContent = text;
    }, 40);
  };

  /* Fetch a section's HTML through the Section Rendering API */
  const fetchSectionHTML = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------- portal + lock */

  /** Moves [data-tb-portal] elements to the end of <body> so fixed overlays
   *  layer above the sticky header and page content. */
  const portal = (scope = document) => {
    qsa('[data-tb-portal]', scope).forEach((el) => {
      if (el.parentElement === document.body) return;
      const existing = el.id ? document.body.querySelector(`:scope > #${CSS.escape(el.id)}`) : null;
      if (existing && existing !== el) existing.remove();
      document.body.appendChild(el);
    });
  };

  let lockCount = 0;
  const lock = () => {
    lockCount += 1;
    document.documentElement.classList.add('tb-locked');
  };
  const unlock = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount) document.documentElement.classList.remove('tb-locked');
  };

  /* ------------------------------------------------------------- drawers */

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  let openDrawer = null;
  let returnFocus = null;

  const focusables = (el) => qsa(FOCUSABLE, el).filter((f) => f.offsetParent !== null || f === document.activeElement);

  const openPanel = (id, opener) => {
    const drawer = document.getElementById(id);
    if (!drawer) return false;
    if (openDrawer === drawer) return true;
    // One drawer handing over to another (quick add → bag) keeps the original
    // control to return focus to when the chain closes.
    const back = returnFocus;
    if (openDrawer) closePanel({ restoreFocus: false });
    returnFocus = back || opener || document.activeElement;
    openDrawer = drawer;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    // next frame so the slide-in transition runs from the closed position
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    lock();
    document.dispatchEvent(new CustomEvent('tb:drawer:open', { detail: { id } }));
    setTimeout(() => {
      const target = qs('[data-autofocus]', drawer) || focusables(qs('.tb-drawer__panel', drawer) || drawer)[0] || qs('.tb-drawer__panel', drawer);
      if (target) target.focus({ preventScroll: true });
    }, 40);
    return true;
  };

  const closePanel = ({ restoreFocus = true } = {}) => {
    const drawer = openDrawer;
    if (!drawer) return;
    openDrawer = null;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    unlock();
    document.dispatchEvent(new CustomEvent('tb:drawer:close', { detail: { id: drawer.id } }));
    const el = returnFocus;
    returnFocus = null;
    if (restoreFocus && el && document.contains(el)) setTimeout(() => el.focus({ preventScroll: true }), 0);
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-tb-open]');
    if (opener) {
      const id = opener.getAttribute('data-tb-open');
      if (document.getElementById(id)) {
        e.preventDefault();
        openPanel(id, opener);
      }
      return;
    }
    if (e.target.closest('[data-tb-close]') && openDrawer) {
      e.preventDefault();
      closePanel();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!openDrawer) return;
    if (e.key === 'Escape') {
      if (document.querySelector('.tb-lightbox:not([hidden])')) return;
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key === 'Tab') {
      const panel = qs('.tb-drawer__panel', openDrawer) || openDrawer;
      const items = focusables(panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  Object.assign(TB, { qs, qsa, debounce, reducedMotion, root, scroller, scrollTop, onScroll, announce, fetchSectionHTML, parseHTML, portal, lock, unlock, openPanel, closePanel, isOpen: (id) => !!openDrawer && openDrawer.id === id });
})();

/* Header: transparent over the homepage hero until scrolled, hovered or a
   panel opens; mega panels open on hover intent, click or keyboard, close on
   Esc, mouse-leave and focus leaving the header. */
(() => {
  const TB = window.TB;
  if (customElements.get('tb-header')) return;

  class TBHeader extends HTMLElement {
    connectedCallback() {
      this.openId = null;
      this.hovering = false;
      this.openTimer = 0;
      this.closeTimer = 0;
      this.overlay = this.dataset.overlay === 'true';

      // Only stay transparent when the page really opens on a hero image
      if (this.overlay) {
        const first = document.querySelector('.content-for-layout > .shopify-section');
        if (!first || !first.querySelector('.tb-hero')) {
          this.overlay = false;
          this.dataset.overlay = 'false';
        }
      }

      this.triggers = TB.qsa('[data-tb-mega]', this);
      this.triggers.forEach((btn) => {
        const id = btn.getAttribute('data-tb-mega');
        btn.addEventListener('click', () => {
          this.clearTimers();
          this.setOpen(this.openId === id ? null : id);
        });
        btn.parentElement.addEventListener('mouseenter', () => this.scheduleOpen(id));
      });
      TB.qsa('[data-tb-nav-link]', this).forEach((a) => a.parentElement.addEventListener('mouseenter', () => this.scheduleOpen(null)));
      TB.qsa('[data-tb-mega-panel]', this).forEach((panel) => panel.addEventListener('mouseenter', () => this.clearTimers()));

      this.addEventListener('mouseenter', () => {
        this.hovering = true;
        this.update();
      });
      this.addEventListener('mouseleave', () => {
        this.hovering = false;
        this.scheduleClose();
        this.update();
      });
      this.addEventListener('focusout', (e) => {
        const next = e.relatedTarget;
        if (next && !this.contains(next)) this.setOpen(null);
      });
      this.onKey = (e) => {
        if (e.key === 'Escape' && this.openId) {
          const id = this.openId;
          this.setOpen(null);
          const trigger = this.querySelector(`[data-tb-mega="${id}"]`);
          if (trigger) trigger.focus();
        }
      };
      document.addEventListener('keydown', this.onKey);
      TB.qsa('[data-tb-mega-panel] a', this).forEach((a) => a.addEventListener('click', () => this.setOpen(null)));

      this.onScroll = () => {
        const scrolled = TB.scrollTop() > 24;
        if (scrolled !== this.scrolled) {
          this.scrolled = scrolled;
          this.update();
        }
      };
      TB.onScroll(this.onScroll);
      this.onScroll();
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('scroll', this.onScroll);
    }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }
    scheduleOpen(id) {
      this.clearTimers();
      this.openTimer = setTimeout(() => this.setOpen(id), this.openId ? 0 : 90);
    }
    scheduleClose() {
      this.clearTimers();
      this.closeTimer = setTimeout(() => this.setOpen(null), 160);
    }

    setOpen(id) {
      if (this.openId === id) return;
      this.openId = id;
      this.triggers.forEach((btn) => {
        const on = btn.getAttribute('data-tb-mega') === id;
        btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        btn.classList.toggle('is-open', on);
      });
      TB.qsa('[data-tb-mega-panel]', this).forEach((panel) => {
        const on = panel.id === id;
        panel.hidden = !on;
        panel.classList.toggle('is-open', on);
      });
      this.classList.toggle('has-panel', !!id);
      this.update();
    }

    update() {
      const solid = !this.overlay || this.scrolled || !!this.openId || this.hovering;
      this.classList.toggle('is-solid', solid);
      this.classList.toggle('is-overlay', !solid);
      this.classList.toggle('tb-on-dark', !solid);
    }
  }
  customElements.define('tb-header', TBHeader);

  /* Phone menu: drill-down panels */
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-tb-mmenu-open]');
    const backBtn = e.target.closest('[data-tb-mmenu-back]');
    if (!openBtn && !backBtn) return;
    const menu = e.target.closest('[data-tb-mmenu]');
    if (!menu) return;
    const rootList = TB.qs('[data-tb-mmenu-root]', menu);
    TB.qsa('[data-tb-mmenu-panel]', menu).forEach((p) => {
      p.hidden = true;
      p.classList.remove('is-entering');
    });
    if (openBtn) {
      const panel = document.getElementById(openBtn.getAttribute('data-tb-mmenu-open'));
      if (!panel) return;
      rootList.hidden = true;
      panel.hidden = false;
      panel.classList.add('is-entering');
      const title = TB.qs('.tb-mmenu__title', panel);
      if (title) title.focus({ preventScroll: true });
      menu.scrollTop = 0;
    } else {
      rootList.hidden = false;
      const first = TB.qs('.tb-mmenu__item', rootList);
      if (first) first.focus({ preventScroll: true });
    }
  });
  // reset the phone menu to its first level whenever it closes
  document.addEventListener('tb:drawer:close', (e) => {
    if (e.detail.id !== 'tb-menu-drawer') return;
    const drawer = document.getElementById('tb-menu-drawer');
    if (!drawer) return;
    TB.qsa('[data-tb-mmenu-panel]', drawer).forEach((p) => (p.hidden = true));
    const rootList = TB.qs('[data-tb-mmenu-root]', drawer);
    if (rootList) rootList.hidden = false;
  });

  /* Accordions (product rows, filter sheet, phone menu groups) */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.tb-accordion__trigger');
    if (!trigger) return;
    const acc = trigger.closest('[data-tb-accordion]');
    if (!acc) return;
    TB.setAccordion(acc, trigger.getAttribute('aria-expanded') !== 'true');
  });
  TB.setAccordion = (acc, open) => {
    const trigger = TB.qs('.tb-accordion__trigger', acc);
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    acc.classList.toggle('is-open', open);
    if (panel) panel.hidden = !open;
  };
})();

/* Search overlay: predictive results as you type (Shopify's predictive search,
   rendered by sections/tb-predictive-search.liquid), popular-search chips, and
   the full results page on submit. */
(() => {
  const TB = window.TB;
  if (customElements.get('tb-search')) return;

  class TBSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = TB.qs('input[type="search"]', this);
      this.status = TB.qs('[data-tb-search-status]', this);
      this.suggest = TB.qs('[data-tb-search-suggest]', this);
      this.results = TB.qs('[data-tb-search-results]', this);
      this.cache = new Map();
      this.controller = null;
      this.lastQuery = '';

      this.onInput = TB.debounce(() => this.search(this.input.value), 180);
      this.input.addEventListener('input', this.onInput);
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-tb-search-term]');
        if (!chip) return;
        this.input.value = chip.getAttribute('data-tb-search-term');
        this.input.focus();
        this.search(this.input.value);
      });
      TB.qs('form', this).addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      });
      document.addEventListener('tb:drawer:close', (e) => {
        if (e.detail.id === 'tb-search-drawer') this.reset();
      });
    }

    reset() {
      if (this.controller) this.controller.abort();
      this.input.value = '';
      this.lastQuery = '';
      this.results.innerHTML = '';
      this.status.textContent = '';
      if (this.suggest) this.suggest.hidden = false;
    }

    async search(raw) {
      const q = raw.trim();
      if (q === this.lastQuery) return;
      this.lastQuery = q;
      if (this.controller) this.controller.abort();
      if (!q) {
        this.results.innerHTML = '';
        this.status.textContent = '';
        if (this.suggest) this.suggest.hidden = false;
        return;
      }
      if (this.suggest) this.suggest.hidden = true;

      let html = this.cache.get(q);
      if (!html) {
        this.controller = new AbortController();
        if (!this.results.children.length) this.results.innerHTML = '<div class="tb-search__loading"><span class="tb-spinner" aria-hidden="true"></span></div>';
        const url = new URL(this.dataset.url || TB.routes.predictive, window.location.origin);
        url.searchParams.set('q', q);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag,vendor,body');
        url.searchParams.set('section_id', 'tb-predictive-search');
        try {
          const res = await fetch(url.toString(), { signal: this.controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          html = await res.text();
          this.cache.set(q, html);
        } catch (err) {
          if (err.name === 'AbortError') return;
          this.results.innerHTML = '';
          this.status.textContent = 'Search is unavailable right now. Press Enter to see all results.';
          return;
        }
      }
      if (q !== this.lastQuery) return;
      const doc = TB.parseHTML(html);
      const body = doc.querySelector('[data-tb-predictive]');
      this.results.innerHTML = body ? body.innerHTML : '';
      const count = body ? Number(body.getAttribute('data-count') || 0) : 0;
      this.status.textContent = count ? `${count} ${count === 1 ? 'result' : 'results'}` : `No matches for ${q}`;
    }
  }
  customElements.define('tb-search', TBSearch);

  // Links inside the overlay close it on the way out (same-page anchors included)
  document.addEventListener('click', (e) => {
    const link = e.target.closest('#tb-search-drawer [data-tb-search-results] a, #tb-menu-drawer a[href]');
    if (link && TB.isOpen(link.closest('[data-tb-drawer]').id)) {
      const url = new URL(link.href, window.location.href);
      if (url.pathname === window.location.pathname && url.search === window.location.search) TB.closePanel({ restoreFocus: false });
    }
  });
})();

/* Bag: Shopify's Ajax cart API with the drawer re-rendered through the
   Section Rendering API (sections/tb-cart-drawer.liquid), so prices,
   discounts and money formats always come from Shopify. */
(() => {
  const TB = window.TB;
  if (TB.cart) return;
  const SECTION = 'tb-cart-drawer';
  const DRAWER = 'tb-cart-drawer';
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  const setCount = (count) => {
    TB.qsa('[data-tb-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    TB.qsa('[data-tb-cart-count-text]').forEach((el) => (el.textContent = `(${count})`));
    TB.qsa('[data-tb-cart-link]').forEach((el) => el.setAttribute('aria-label', `Bag, ${count} ${count === 1 ? 'item' : 'items'}`));
  };

  /** Swap the drawer contents for freshly rendered ones. */
  const render = (html) => {
    if (!html) return null;
    const next = TB.parseHTML(html).getElementById('tb-cart-contents');
    const cur = document.getElementById('tb-cart-contents');
    if (!next) return null;
    if (cur) cur.replaceWith(next);
    setCount(Number(next.getAttribute('data-count') || 0));
    return next;
  };

  const refresh = async () => {
    try {
      render(await TB.fetchSectionHTML(TB.routes.root, SECTION));
    } catch (e) {
      /* keep what is shown */
    }
  };

  const errorFrom = async (res) => {
    let msg = 'Something went wrong. Please try again.';
    try {
      const data = await res.json();
      msg = data.description || data.message || msg;
    } catch (e) {
      /* not JSON */
    }
    const err = new Error(msg);
    err.status = res.status;
    return err;
  };

  const afterChange = () => {
    // Heritage's cart page lists the lines itself: reload it to stay in step
    if (TB.pageType === 'cart') window.location.reload();
    document.dispatchEvent(new CustomEvent('tb:cart:change'));
  };

  /**
   * Add to the bag. `body` is a FormData from a product form (keeps any line
   * item properties added by apps) or { items: [{ id, quantity }] }.
   */
  const add = async (body, { open = true, variantId, title, quantity = 1, opener } = {}) => {
    let init;
    if (body instanceof FormData) {
      body.append('sections', SECTION);
      body.append('sections_url', window.location.pathname);
      init = { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    } else {
      init = { method: 'POST', body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }), headers: jsonHeaders };
    }
    const res = await fetch(`${TB.routes.cartAdd}.js`, init);
    if (!res.ok) throw await errorFrom(res);
    const data = await res.json();
    const contents = render(data.sections && data.sections[SECTION]);
    if (!contents) await refresh();
    const line = variantId && document.querySelector(`#tb-cart-contents .tb-bag__line[data-variant-id="${variantId}"]`);
    if (line) {
      const note = TB.qs('[data-tb-added]', line);
      if (note) note.hidden = false;
    }
    TB.announce(`Added ${quantity} × ${title || 'item'} to your bag.`);
    afterChange();
    if (open && TB.pageType !== 'cart') TB.openPanel(DRAWER, opener);
    return data;
  };

  const change = async (line, quantity) => {
    const res = await fetch(`${TB.routes.cartChange}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ line, quantity, sections: SECTION, sections_url: window.location.pathname }),
    });
    if (!res.ok) throw await errorFrom(res);
    const data = await res.json();
    if (!render(data.sections && data.sections[SECTION])) await refresh();
    afterChange();
    return data;
  };

  /* Quantity and remove buttons inside the drawer */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#tb-cart-contents [data-tb-cart-qty]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const lineEl = btn.closest('.tb-bag__line');
    const key = lineEl && lineEl.getAttribute('data-key');
    const role = btn.getAttribute('data-tb-cart-role');
    const title = lineEl ? (TB.qs('.tb-bag__name', lineEl) || {}).textContent : '';
    const qty = Number(btn.getAttribute('data-tb-cart-qty'));
    if (lineEl) lineEl.classList.add('is-busy');
    try {
      await change(Number(btn.getAttribute('data-line')), qty);
      TB.announce(qty === 0 ? `Removed ${title} from your bag.` : `Quantity of ${title} is now ${qty}.`);
      // keep keyboard focus on the equivalent control after the re-render
      const again = key && document.querySelector(`#tb-cart-contents .tb-bag__line[data-key="${CSS.escape(key)}"] [data-tb-cart-role="${role}"]`);
      const drawer = document.getElementById(DRAWER);
      if (again && !again.disabled) again.focus({ preventScroll: true });
      else if (drawer) (TB.qs('.tb-drawer__panel', drawer) || drawer).focus({ preventScroll: true });
    } catch (err) {
      if (lineEl) lineEl.classList.remove('is-busy');
      const box = TB.qs('#tb-cart-contents [data-tb-cart-error]');
      if (box) box.textContent = err.message;
      TB.announce(err.message);
    }
  });

  // Refresh when the page is restored from the back/forward cache
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh();
  });

  TB.cart = { add, change, refresh, render, setCount };
})();

/* Quick add on product cards: adds straight away when a product has a single
   variant; otherwise opens the option chooser (a side panel on desktop, a
   bottom sheet on phones) rendered by sections/tb-quick-add.liquid. */
(() => {
  const TB = window.TB;
  const DRAWER = 'tb-quick-drawer';
  const cache = new Map();

  const loading = '<div class="tb-drawer__loading"><span class="tb-spinner" aria-hidden="true"></span><span class="tb-visually-hidden">Loading</span></div>';

  const openChooser = async (btn, message) => {
    const drawer = document.getElementById(DRAWER);
    if (!drawer) {
      window.location.href = btn.getAttribute('data-url');
      return;
    }
    const body = TB.qs('[data-tb-quick-body]', drawer);
    const url = btn.getAttribute('data-url');
    body.innerHTML = loading;
    TB.openPanel(DRAWER, btn);
    try {
      let html = cache.get(url);
      if (!html) {
        html = await TB.fetchSectionHTML(url, 'tb-quick-add');
        cache.set(url, html);
      }
      if (!TB.isOpen(DRAWER)) return;
      const content = TB.parseHTML(html).querySelector('[data-tb-quick-content]');
      if (!content) throw new Error('No content');
      body.innerHTML = '';
      body.appendChild(content);
      if (message) {
        const err = TB.qs('[data-tb-buy-error]', content);
        if (err) err.textContent = message;
      }
      const first = TB.qs('.tb-value__input:not(:disabled), [data-tb-add]', content);
      if (first) first.focus({ preventScroll: true });
    } catch (e) {
      window.location.href = url;
    }
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-tb-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('data-tb-quick-add') === 'choose') {
      openChooser(btn);
      return;
    }
    if (btn.classList.contains('is-busy')) return;
    btn.classList.add('is-busy');
    const card = btn.closest('.tb-card');
    const title = card ? (TB.qs('.tb-card__title', card) || {}).textContent : '';
    const variantId = Number(btn.getAttribute('data-variant-id'));
    try {
      await TB.cart.add({ items: [{ id: variantId, quantity: 1 }] }, { variantId, title: (title || '').trim(), opener: btn });
    } catch (err) {
      openChooser(btn, err.message);
    } finally {
      btn.classList.remove('is-busy');
    }
  });

  // a fresh chooser each time: availability can change between openings
  document.addEventListener('tb:cart:change', () => cache.clear());
})();

/* Homepage behaviour: the slow auto-scrolling bands, the edit's tabs and the
   look banner's shoppable dots. */
(() => {
  const TB = window.TB;

  /* ---------------------------------------------------------- auto scroller
     A slow, seamless leftward drift for decorative content (the text marquee
     and the editorial image strip).
     - One set of items is real; the copies that make the loop seamless are
       aria-hidden and inert.
     - Pauses on mouse hover, on keyboard focus, while off-screen, and with the
       pause control (floating over the band, or in a row after it).
     - Touch: a horizontal swipe scrubs the strip; vertical scrolling stays native.
     - prefers-reduced-motion: nothing moves; a static equivalent is shown
       ('scroll': a row you can scroll yourself, 'wrap': a centred, wrapped row). */
  if (!customElements.get('tb-auto-scroller')) {
    class TBAuto extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.viewport = TB.qs('.tb-auto__viewport', this);
        this.track = TB.qs('.tb-auto__track', this);
        this.set = TB.qs('.tb-auto__set', this);
        this.button = TB.qs('.tb-auto__toggle, .tb-auto__control', this);
        this.speed = Number(this.dataset.speed || 24);
        this.staticMode = this.dataset.static || 'scroll';
        this.overlay = this.dataset.placement !== 'after';
        this.m = { offset: 0, setWidth: 0, hover: false, focus: false, dragging: false, resumeAt: 0, inView: true, paused: false };
        this.focusPaused = false;
        this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onMotionPref = () => this.setup();
        this.mq.addEventListener('change', this.onMotionPref);

        if (this.button) this.button.addEventListener('click', () => this.toggle());
        this.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.m.hover = true;
        });
        this.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') this.m.hover = false;
        });
        this.addEventListener('focusin', (e) => {
          let visible = false;
          try {
            visible = e.target.matches(':focus-visible');
          } catch (err) {
            visible = true;
          }
          if (visible && (this.overlay || e.target.closest('.tb-auto__viewport'))) {
            this.m.focus = true;
            this.focusPaused = true;
            this.paint();
          }
        });
        this.addEventListener('focusout', () => {
          this.m.focus = false;
          this.focusPaused = false;
          this.paint();
        });
        this.viewport.addEventListener('pointerdown', (e) => this.down(e));
        this.viewport.addEventListener('pointermove', (e) => this.move(e));
        this.viewport.addEventListener('pointerup', (e) => this.up(e));
        this.viewport.addEventListener('pointercancel', (e) => this.up(e));
        this.setup();
      }

      disconnectedCallback() {
        this.teardown();
        if (this.mq) this.mq.removeEventListener('change', this.onMotionPref);
        this.ready = false;
      }

      teardown() {
        cancelAnimationFrame(this.raf);
        if (this.ro) this.ro.disconnect();
        if (this.io) this.io.disconnect();
        TB.qsa('.tb-auto__set[aria-hidden="true"]', this.track).forEach((c) => c.remove());
        this.track.style.transform = '';
      }

      setup() {
        this.teardown();
        if (this.mq.matches) {
          this.classList.add('is-static');
          this.classList.remove('is-ready');
          if (this.staticMode === 'scroll') this.viewport.tabIndex = 0;
          return;
        }
        this.classList.remove('is-static');
        this.viewport.removeAttribute('tabindex');

        const measure = () => {
          const w = this.set.getBoundingClientRect().width;
          if (w <= 0) return;
          this.m.setWidth = w;
          this.m.offset = ((this.m.offset % w) + w) % w;
          const need = Math.max(1, Math.ceil(this.viewport.clientWidth / w));
          const clones = TB.qsa('.tb-auto__set[aria-hidden="true"]', this.track);
          for (let i = clones.length; i < need; i += 1) {
            const c = this.set.cloneNode(true);
            c.setAttribute('aria-hidden', 'true');
            c.setAttribute('inert', '');
            TB.qsa('[id]', c).forEach((n) => n.removeAttribute('id'));
            this.track.appendChild(c);
          }
          clones.slice(need).forEach((c) => c.remove());
        };
        measure();
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(measure);
          this.ro.observe(this.set);
          this.ro.observe(this.viewport);
        }
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(([entry]) => {
            this.m.inView = entry.isIntersecting;
          });
          this.io.observe(this.viewport);
        }
        this.classList.add('is-ready');
        this.paint();

        let last = performance.now();
        let applied = NaN;
        const tick = (now) => {
          const dt = Math.min(0.05, (now - last) / 1000);
          last = now;
          const m = this.m;
          const running = !m.paused && !m.hover && !m.focus && !m.dragging && m.inView && now >= m.resumeAt && m.setWidth > 0;
          if (running) m.offset = (m.offset + this.speed * dt) % m.setWidth;
          if (m.offset !== applied) {
            this.track.style.transform = `translate3d(${-m.offset}px, 0, 0)`;
            applied = m.offset;
          }
          this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
      }

      wrap(v) {
        const w = this.m.setWidth || 1;
        return ((v % w) + w) % w;
      }
      down(e) {
        if (e.pointerType === 'mouse' || this.mq.matches) return;
        this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, offset: this.m.offset, decided: false, horizontal: false };
      }
      move(e) {
        const d = this.drag;
        if (!d || d.id !== e.pointerId) return;
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (!d.decided) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          d.decided = true;
          d.horizontal = Math.abs(dx) > Math.abs(dy);
          if (!d.horizontal) {
            this.drag = null; // a vertical gesture: leave it to the page
            return;
          }
          this.m.dragging = true;
          try {
            this.viewport.setPointerCapture(e.pointerId);
          } catch (err) {
            /* pointer already gone */
          }
        }
        this.m.offset = this.wrap(d.offset - dx);
      }
      up(e) {
        const d = this.drag;
        if (!d || d.id !== e.pointerId) return;
        this.drag = null;
        if (this.m.dragging) {
          this.m.dragging = false;
          this.m.resumeAt = performance.now() + 2500;
        }
      }

      toggle() {
        // Pressing play while focused on the control is explicit intent: resume now.
        if (this.m.paused || this.focusPaused) {
          this.m.paused = false;
          this.focusPaused = false;
          this.m.focus = false;
        } else {
          this.m.paused = true;
        }
        this.paint();
      }

      paint() {
        if (!this.button) return;
        const paused = this.m.paused || this.focusPaused;
        this.button.setAttribute('data-state', paused ? 'paused' : 'playing');
        if (this.overlay) {
          this.button.setAttribute('aria-pressed', paused ? 'true' : 'false');
          const text = TB.qs('.tb-auto__toggle-text', this.button);
          if (text) text.textContent = paused ? 'Play' : 'Pause';
        } else {
          const text = TB.qs('[data-tb-auto-label]', this.button);
          if (text) text.textContent = paused ? 'Play motion' : 'Pause motion';
        }
      }
    }
    customElements.define('tb-auto-scroller', TBAuto);
  }

  /* -------------------------------------------------------------- edit tabs */
  if (!customElements.get('tb-tabs')) {
    class TBTabs extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.tabs = TB.qsa('[role="tab"]', this);
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            const n = this.tabs.length;
            const next = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
            if (next < 0) return;
            e.preventDefault();
            this.select(next);
            this.tabs[next].focus();
          });
        });
      }
      select(index, { animate = true } = {}) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (!panel) return;
          const wasHidden = panel.hidden;
          panel.hidden = !on;
          if (on && wasHidden && animate) {
            panel.classList.remove('is-entering');
            void panel.offsetWidth;
            panel.classList.add('is-entering');
          }
        });
        TB.qsa('[data-tb-tab-foot]', this).forEach((foot) => (foot.hidden = foot.getAttribute('data-tb-tab-foot') !== String(index)));
      }
    }
    customElements.define('tb-tabs', TBTabs);
  }

  /* ------------------------------------------------------- look hotspots */
  if (!customElements.get('tb-hotspots')) {
    class TBHotspots extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.spots = TB.qsa('.tb-spot', this);
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('.tb-spot__btn');
          if (!btn) return;
          const spot = btn.closest('.tb-spot');
          this.open(spot.classList.contains('is-open') ? null : spot);
        });
        this.onDown = (e) => {
          if (this.current && !e.target.closest('.tb-spot')) this.open(null);
        };
        this.onKey = (e) => {
          if (e.key === 'Escape' && this.current) {
            const btn = TB.qs('.tb-spot__btn', this.current);
            this.open(null);
            btn.focus();
          }
        };
        document.addEventListener('mousedown', this.onDown);
        document.addEventListener('keydown', this.onKey);
      }
      disconnectedCallback() {
        document.removeEventListener('mousedown', this.onDown);
        document.removeEventListener('keydown', this.onKey);
        this.ready = false;
      }
      open(spot) {
        this.current = spot;
        this.spots.forEach((s) => {
          const on = s === spot;
          s.classList.toggle('is-open', on);
          TB.qs('.tb-spot__btn', s).setAttribute('aria-expanded', on ? 'true' : 'false');
          const card = TB.qs('.tb-spot__card', s);
          if (card) card.hidden = !on;
        });
      }
    }
    customElements.define('tb-hotspots', TBHotspots);
  }
})();

/* Product form, shared by the product page and quick add.
   Options are native radio groups. Options with a single value are chosen for
   the shopper; the others must be chosen — pressing Add to bag first shows
   what is missing instead of guessing. Every change updates the price,
   availability and buttons in the surrounding [data-tb-product-scope] and
   fires `tb:variant:change` for the gallery, sticky bar and complete-the-look. */
(() => {
  const TB = window.TB;
  if (customElements.get('tb-product-form')) return;

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  /** A variant's saving in whole percent: from the price element's data-off-map
   *  (worked out in Liquid), else from the two displayed prices. */
  const savedPercent = (el, v) => {
    const hit = (el.getAttribute('data-off-map') || '').split(',').find((pair) => pair.split(':')[0] === String(v.id));
    if (hit) return Number(hit.split(':')[1]) || 0;
    const was = Number(String(v.compare).replace(/\D/g, ''));
    const now = Number(String(v.price).replace(/\D/g, ''));
    return was > now ? Math.round(((was - now) * 100) / was) : 0;
  };
  /** Price markup for a variant. Elements with data-off (the product page) show
   *  the compare-at price first, then the price and the saving in red. */
  const priceHTML = (v, el) => {
    if (!v.compare) return `<span>${v.price}</span>`;
    const now = `<span class="tb-price__now"><span class="tb-visually-hidden">Sale price </span>${v.price}</span>`;
    const was = `<s class="tb-price__was"><span class="tb-visually-hidden">Regular price </span>${v.compare}</s>`;
    const off = el && el.getAttribute('data-off');
    if (!off) return `${now} ${was}`;
    const pct = savedPercent(el, v);
    const label = pct > 0 ? ` <span class="tb-price__off"><span class="tb-visually-hidden">Discount </span>${esc(off.replace('[percent]', pct))}</span>` : '';
    return `${was} ${now}${label}`;
  };

  class TBProductForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const json = TB.qs('[data-tb-product-json]', this);
      if (!json) return;
      this.ready = true;
      this.data = JSON.parse(json.textContent);
      this.scope = this.closest('[data-tb-product-scope]') || this;
      this.form = TB.qs('form[action*="/cart/add"]', this) || TB.qs('form', this);
      this.idInput = TB.qs('[data-tb-variant-input]', this);
      this.qtyInput = TB.qs('[data-tb-qty-input]', this);
      this.addBtn = TB.qs('[data-tb-add]', this);
      this.buyBtn = TB.qs('[data-tb-buy-now]', this);
      this.status = TB.qs('[data-tb-buy-status]', this);
      this.error = TB.qs('[data-tb-buy-error]', this);
      this.fieldsets = TB.qsa('[data-tb-option]', this);
      this.maxQty = Number(this.dataset.maxQty || 10);
      this.tried = false;
      this.addedTimer = 0;
      const range = TB.qs('template[data-tb-price-range]', this.scope);
      const rangeEl = range && range.content ? range.content.firstElementChild : null;
      this.rangeHTML = rangeEl ? rangeEl.innerHTML : null;
      const panel = this.closest('[data-tb-panel]');
      if (panel && 'ResizeObserver' in window) {
        // lets CSS pin a tall purchase panel by its bottom instead of its top
        new ResizeObserver(() => panel.style.setProperty('--panel-h', `${panel.offsetHeight}px`)).observe(panel);
      }

      this.sel = this.data.options.map((o, i) => {
        const checked = this.querySelector(`[data-tb-option="${i}"] input:checked`);
        return checked ? checked.value : o.values.length === 1 ? o.values[0] : null;
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-tb-option] input[type="radio"]');
        if (!input) return;
        const i = Number(input.closest('[data-tb-option]').getAttribute('data-tb-option'));
        this.sel[i] = input.value;
        if (this.error) this.error.textContent = '';
        this.update(true);
      });
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-tb-qty-step]');
        if (step) this.setQty(this.qty() + Number(step.getAttribute('data-tb-qty-step')));
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onAdd(e));
      if (this.buyBtn) this.buyBtn.addEventListener('click', () => this.onBuyNow());
      this.scope.addEventListener('click', (e) => {
        if (e.target.closest('[data-tb-buybar-add]')) {
          e.preventDefault();
          if (this.form) this.form.requestSubmit ? this.form.requestSubmit(this.addBtn || undefined) : this.onAdd();
        }
      });
      this.applyCarry();
      this.setQty(this.qty());
      this.update(false);
    }

    /** Coming from a sibling colour: keep the shopper's choices and scroll position */
    applyCarry() {
      if (this.dataset.context !== 'pdp') return;
      let carry = null;
      try {
        carry = JSON.parse(sessionStorage.getItem('tb:carry') || 'null');
        sessionStorage.removeItem('tb:carry');
      } catch (e) {
        carry = null;
      }
      if (!carry || Date.now() - carry.t > 20000) return;
      this.data.options.forEach((o, i) => {
        const v = carry.sel && carry.sel[o.name];
        if (v && o.values.includes(v) && this.sel[i] === null) {
          this.sel[i] = v;
          const input = [...this.querySelectorAll(`[data-tb-option="${i}"] input`)].find((x) => x.value === v);
          if (input) input.checked = true;
        }
      });
      if (carry.scroll) requestAnimationFrame(() => (TB.scroller().scrollTop = carry.scroll));
    }

    /* ------------------------------------------------------------ state */

    qty() {
      return Math.max(1, Math.min(this.maxQty, Number(this.qtyInput ? this.qtyInput.value : 1) || 1));
    }
    setQty(n) {
      const q = Math.max(1, Math.min(this.maxQty, n));
      if (!this.qtyInput) return;
      this.qtyInput.value = q;
      const out = TB.qs('[data-tb-qty-output]', this);
      if (out) {
        out.textContent = q;
        out.setAttribute('aria-label', `Quantity ${q}`);
      }
      const dec = TB.qs('[data-tb-qty-step="-1"]', this);
      const inc = TB.qs('[data-tb-qty-step="1"]', this);
      if (dec) dec.disabled = q <= 1;
      if (inc) inc.disabled = q >= this.maxQty;
    }
    missingIndex() {
      return this.sel.findIndex((v) => v === null);
    }
    get complete() {
      return this.missingIndex() === -1;
    }
    get variant() {
      if (!this.complete) return null;
      return this.data.variants.find((v) => v.options.every((o, i) => o === this.sel[i])) || null;
    }
    productAvailable() {
      return this.data.variants.some((v) => v.available);
    }
    /** Is there an available variant with this value, given the other chosen options? */
    valueAvailable(index, value) {
      return this.data.variants.some((v) => v.available && v.options[index] === value && v.options.every((o, i) => i === index || this.sel[i] === null || this.sel[i] === o));
    }
    missingName() {
      const i = this.missingIndex();
      return (i >= 0 ? this.data.options[i].label || this.data.options[i].name : 'option').toLowerCase();
    }
    /** The shopper-facing label of an option value ("Tie + pocket square" for "TIE AND POCKET HANKEY") */
    label(i, value) {
      const opt = this.data.options[i];
      const at = opt.values.indexOf(value);
      return (opt.labels && at >= 0 && opt.labels[at]) || value;
    }

    /* --------------------------------------------------------------- UI */

    update(fromUser) {
      const v = this.variant;
      const complete = this.complete;
      const soldOut = complete ? !v || !v.available : !this.productAvailable();

      // option legends + availability marks
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.getAttribute('data-tb-option'));
        const opt = this.data.options[i];
        const valueEl = TB.qs('[data-tb-option-value]', fs);
        if (valueEl) valueEl.textContent = this.sel[i] ? this.label(i, this.sel[i]) : opt.values.length > 1 ? 'Choose one' : '';
        TB.qsa('.tb-value', fs).forEach((box) => {
          const input = TB.qs('input', box);
          const ok = this.valueAvailable(i, input.value);
          box.classList.toggle('is-unavailable', !ok);
          const soldTag = TB.qs('[data-tb-soldout]', box);
          if (soldTag) soldTag.hidden = ok;
        });
        if (this.tried && this.sel[i] !== null) this.setInvalid(fs, false);
      });

      // hidden id: the chosen variant, or the first available one for apps that read it
      if (this.idInput) this.idInput.value = v ? v.id : this.idInput.getAttribute('data-default');

      // price
      TB.qsa('[data-tb-price]', this.scope).forEach((el) => {
        if (v) el.innerHTML = priceHTML(v, el);
        else if (this.rangeHTML !== null) el.innerHTML = this.rangeHTML;
      });

      // availability
      const partial = !complete && this.productAvailable() && this.data.variants.some((x) => !x.available);
      TB.qsa('[data-tb-avail]', this.scope).forEach((el) => {
        el.classList.toggle('is-out', soldOut);
        const t = TB.qs('[data-tb-avail-text]', el);
        if (t) t.textContent = soldOut ? 'Sold out' : partial ? 'In stock · some options sold out' : 'In stock';
      });

      // buttons
      const label = soldOut ? 'Sold out' : complete ? 'Add to bag' : `Choose ${this.missingName()}`;
      if (this.addBtn && !this.addBtn.classList.contains('is-added')) this.setAddLabel(label);
      if (this.addBtn) {
        if (soldOut) this.addBtn.setAttribute('aria-disabled', 'true');
        else this.addBtn.removeAttribute('aria-disabled');
      }
      TB.qsa('[data-tb-buybar-add]', this.scope).forEach((b) => {
        b.textContent = label;
        if (soldOut) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
      });
      if (this.buyBtn) this.buyBtn.disabled = soldOut;
      if (this.status) this.status.textContent = soldOut ? 'This option is sold out.' : complete ? '' : `Choose a ${this.missingName()} before adding to your bag.`;

      // facts that depend on the option: a pocket square only some options include, the SKU
      if (this.data.set_option !== null && this.data.set_option !== undefined) {
        const hasSquare = !v || (v.contents || []).includes('square');
        TB.qsa('[data-tb-needs="square"]', this.scope).forEach((el) => (el.hidden = !hasSquare));
      }
      if (v) {
        TB.qsa('[data-tb-sku]', this.scope).forEach((row) => {
          const dd = TB.qs('dd', row);
          if (dd) dd.textContent = v.sku || '';
          row.hidden = !v.sku;
        });
      }

      // shareable URL on the product page
      if (fromUser && this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        if (v) url.searchParams.set('variant', v.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      }

      this.scope.dispatchEvent(
        new CustomEvent('tb:variant:change', {
          bubbles: true,
          detail: { variant: v, selection: this.sel.slice(), complete, product: this.data, fromUser },
        }),
      );
    }

    setAddLabel(label, added) {
      if (!this.addBtn) return;
      const span = TB.qs('[data-tb-add-label]', this.addBtn);
      const check = TB.qs('[data-tb-add-check]', this.addBtn);
      if (span) span.textContent = label;
      if (check) check.hidden = !added;
    }

    setInvalid(fs, on) {
      fs.classList.toggle('is-invalid', on);
      const err = TB.qs('.tb-option__error', fs);
      if (err) err.hidden = !on;
      if (on) fs.setAttribute('aria-describedby', err ? err.id : '');
      else fs.removeAttribute('aria-describedby');
    }

    requireComplete() {
      this.tried = true;
      const missing = this.missingIndex();
      if (missing === -1) return true;
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.getAttribute('data-tb-option'));
        this.setInvalid(fs, this.sel[i] === null);
      });
      const fs = this.querySelector(`[data-tb-option="${missing}"]`);
      const input = fs && TB.qs('input:not(:disabled)', fs);
      if (input) {
        input.scrollIntoView({ block: 'center', behavior: TB.reducedMotion() ? 'auto' : 'smooth' });
        setTimeout(() => input.focus({ preventScroll: true }), 250);
      }
      return false;
    }

    /* ---------------------------------------------------------- actions */

    async onAdd(e) {
      if (e) e.preventDefault();
      if (this.busy) return;
      if (!this.requireComplete()) return;
      const v = this.variant;
      if (!v || !v.available) return;
      if (this.error) this.error.textContent = '';
      this.busy = true;
      this.addBtn && this.addBtn.setAttribute('aria-busy', 'true');
      const quantity = this.qty();
      try {
        await TB.cart.add(new FormData(this.form), { variantId: v.id, title: this.data.title, quantity, opener: this.dataset.context === 'quick' ? null : this.addBtn });
        if (this.addBtn && this.dataset.context !== 'quick') {
          this.addBtn.classList.add('is-added');
          this.setAddLabel('Added to bag', true);
          clearTimeout(this.addedTimer);
          this.addedTimer = setTimeout(() => {
            this.addBtn.classList.remove('is-added');
            this.update(false);
          }, 2400);
        }
      } catch (err) {
        if (this.error) this.error.textContent = err.message;
        TB.announce(err.message);
      } finally {
        this.busy = false;
        this.addBtn && this.addBtn.removeAttribute('aria-busy');
      }
    }

    async onBuyNow() {
      if (this.busy) return;
      if (!this.requireComplete()) return;
      const v = this.variant;
      if (!v || !v.available) return;
      this.busy = true;
      this.buyBtn.setAttribute('aria-busy', 'true');
      try {
        const cart = await fetch(`${TB.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        if (!cart.items.some((item) => item.variant_id === v.id)) {
          await TB.cart.add(new FormData(this.form), { open: false, variantId: v.id, title: this.data.title, quantity: this.qty() });
        }
        window.location.href = '/checkout';
      } catch (err) {
        if (this.error) this.error.textContent = err.message;
        TB.announce(err.message);
        this.busy = false;
        this.buyBtn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('tb-product-form', TBProductForm);

  // Colour-family links swap the product in place of the current one
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-tb-sibling]');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    e.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;
    const form = document.querySelector('tb-product-form[data-context="pdp"]');
    const sel = {};
    if (form && form.data) form.data.options.forEach((o, i) => { if (form.sel[i] !== null) sel[o.name] = form.sel[i]; });
    try {
      sessionStorage.setItem('tb:carry', JSON.stringify({ sel, scroll: TB.scrollTop(), t: Date.now() }));
    } catch (err) {
      /* storage unavailable: the link still works */
    }
    window.location.replace(link.href);
  });

  /* --------------------------------------------------------- sticky buy bar */

  if (!customElements.get('tb-buybar')) {
    class TBBuyBar extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.scope = this.closest('[data-tb-product-scope]') || document;
        this.check = () => {
          const btn = TB.qs('[data-tb-add]', this.scope);
          if (!btn) return;
          const footer = document.querySelector('.tb-footer');
          const past = btn.getBoundingClientRect().bottom < 0;
          const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight - 40 : false;
          this.setVisible(past && !nearFooter);
        };
        TB.onScroll(this.check);
        window.addEventListener('resize', this.check);
        this.scope.addEventListener('tb:variant:change', (e) => {
          const v = e.detail.variant;
          const img = TB.qs('img', this);
          if (v && v.image && img) {
            img.removeAttribute('srcset');
            img.src = v.image;
          }
        });
        this.check();
      }
      setVisible(on) {
        if (on === this.visible) return;
        this.visible = on;
        this.classList.toggle('is-visible', on);
        this.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) this.removeAttribute('inert');
        else this.setAttribute('inert', '');
        document.body.classList.toggle('tb-has-buybar', on);
      }
      disconnectedCallback() {
        document.body.classList.remove('tb-has-buybar');
      }
    }
    customElements.define('tb-buybar', TBBuyBar);
  }

  /* ------------------------------------------------------ PIN code check
     Any valid Indian PIN code is shown as deliverable — no delivery date.
     Valid: six digits whose first two are a real India Post zone
     (11–19, 20–28, 30–34, 36–39, 40–49, 50–53, 56–59, 60–64, 67–69,
     70–79, 80–85, 90–99). */
  const PIN = /^(1[1-9]|2[0-8]|3[0-46-9]|4\d|5[0-36-9]|6[0-47-9]|7\d|8[0-5]|9\d)\d{4}$/;
  // used when the section markup has no icon template (an older tb-main-product)
  const CHECK_ICON = '<svg class="tb-icon tb-pin__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>';

  const pinState = (form, state, text) => {
    const input = TB.qs('input', form);
    const msg = TB.qs('[data-tb-pin-message]', form);
    TB.qs('.tb-pin__field', form).classList.toggle('is-invalid', state === 'error');
    if (state === 'error') input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    msg.classList.toggle('is-error', state === 'error');
    msg.classList.toggle('is-success', state === 'success');
    msg.textContent = '';
    if (state === 'success') {
      const icon = TB.qs('template[data-tb-pin-icon]', form);
      if (icon) msg.appendChild(icon.content.cloneNode(true));
      else msg.insertAdjacentHTML('beforeend', CHECK_ICON);
      const span = document.createElement('span');
      span.textContent = text;
      msg.appendChild(span);
    } else {
      msg.textContent = text;
    }
  };

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-tb-pin] input');
    if (!input) return;
    const clean = input.value.replace(/\D/g, '').slice(0, 6);
    if (clean !== input.value) input.value = clean;
    const form = input.closest('[data-tb-pin]');
    pinState(form, 'idle', form.getAttribute('data-prompt'));
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-tb-pin]');
    if (!form) return;
    e.preventDefault();
    const pin = TB.qs('input', form).value.trim();
    if (!PIN.test(pin)) {
      pinState(form, 'error', form.getAttribute('data-invalid'));
      return;
    }
    pinState(form, 'success', (form.getAttribute('data-success') || 'Delivery available to [pin].').replace('[pin]', pin));
  });
})();

/* Product gallery: lead image and a two-up grid on desktop, a swipeable
   carousel with a counter and dots on phones. Choosing a colour narrows it to
   that colourway; other options bring their own image to the front. Any image
   opens the zoom lightbox. */
(() => {
  const TB = window.TB;
  if (customElements.get('tb-gallery')) return;
  const ZOOM = 2.4;

  class TBGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = TB.qs('[data-tb-gallery-track]', this);
      this.count = TB.qs('[data-tb-gallery-count]', this);
      this.dots = TB.qs('[data-tb-gallery-dots]', this);
      this.original = TB.qsa('.tb-gallery__slide', this.track);
      this.slide = 0;
      if (!this.track) return;

      this.track.addEventListener('scroll', () => {
        const i = Math.round(this.track.scrollLeft / Math.max(1, this.slideWidth()));
        if (i !== this.slide) {
          this.slide = i;
          this.paintPager();
        }
      }, { passive: true });
      this.track.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tb-zoom]');
        if (!btn) return;
        const items = this.zoomable();
        const index = items.indexOf(btn.closest('.tb-gallery__slide'));
        if (index >= 0) openLightbox(items, index, btn, this.getAttribute('data-title') || '');
      });
      const scope = this.closest('[data-tb-product-scope]');
      if (scope) scope.addEventListener('tb:variant:change', (e) => this.onVariant(e.detail));
      this.paintPager();
    }

    slideWidth() {
      const first = this.visible()[0];
      return first ? first.getBoundingClientRect().width : this.track.clientWidth;
    }
    visible() {
      return TB.qsa('.tb-gallery__slide', this.track).filter((s) => !s.hidden);
    }
    zoomable() {
      return this.visible().filter((s) => s.querySelector('[data-tb-zoom]'));
    }

    onVariant({ variant, selection, product }) {
      const ci = product.colour_option;
      const colour = ci !== null && ci !== undefined && selection[ci] ? selection[ci].toLowerCase() : '';
      const media = TB.qsa('.tb-gallery__slide[data-media-id]', this.track);
      let order = this.original.slice();

      if (colour) {
        const narrowed = media.filter((s) => !s.dataset.colour || s.dataset.colour === colour);
        media.forEach((s) => (s.hidden = narrowed.length ? !narrowed.includes(s) : false));
      } else {
        media.forEach((s) => (s.hidden = false));
        if (variant && variant.media) {
          const lead = order.find((s) => s.dataset.mediaId === String(variant.media));
          if (lead) order = [lead, ...order.filter((s) => s !== lead)];
        }
      }
      // keep the measurements panel last
      order.sort((a, b) => (a.classList.contains('tb-gallery__slide--measure') ? 1 : 0) - (b.classList.contains('tb-gallery__slide--measure') ? 1 : 0));
      const current = TB.qsa('.tb-gallery__slide', this.track);
      if (order.some((s, i) => current[i] !== s)) order.forEach((s) => this.track.appendChild(s));

      const shown = this.visible();
      shown.forEach((s, i) => {
        s.classList.toggle('tb-gallery__slide--lead', i === 0);
        s.setAttribute('aria-label', `${i + 1} of ${shown.length}`);
        const btn = TB.qs('[data-tb-zoom]', s);
        if (btn) btn.setAttribute('aria-label', `Enlarge image ${i + 1} of ${shown.length}${btn.dataset.alt ? `: ${btn.dataset.alt}` : ''}`);
      });
      if (this.track.scrollWidth > this.track.clientWidth + 4) this.track.scrollTo({ left: 0, behavior: TB.reducedMotion() ? 'auto' : 'smooth' });
      this.slide = 0;
      this.paintPager();
    }

    paintPager() {
      const total = this.visible().length;
      if (this.count) this.count.textContent = `${Math.min(this.slide + 1, total)} / ${total}`;
      if (!this.dots) return;
      if (this.dots.children.length !== total) {
        this.dots.innerHTML = '<span class="tb-gallery__dot"></span>'.repeat(total);
      }
      Array.from(this.dots.children).forEach((d, i) => d.classList.toggle('is-active', i === this.slide));
    }
  }
  customElements.define('tb-gallery', TBGallery);

  /* ---------------------------------------------------------------- lightbox */

  const icon = (name, size) => {
    const tpl = document.getElementById(`tb-icon-${name}`);
    return tpl ? tpl.innerHTML.replace(/width="\d+"/, `width="${size}"`).replace(/height="\d+"/, `height="${size}"`) : '';
  };

  let box = null;

  function openLightbox(slides, start, opener, title) {
    const images = slides.map((s) => {
      const btn = TB.qs('[data-tb-zoom]', s);
      return { src: btn.dataset.zoomSrc, thumb: btn.dataset.thumbSrc, alt: btn.dataset.alt || '' };
    });
    if (box) box.remove();
    box = document.createElement('div');
    box.className = 'tb-lightbox tb-root';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.innerHTML = `
      <div class="tb-lightbox__bar">
        <p class="tb-lightbox__count tb-num" aria-live="polite"></p>
        <div class="tb-lightbox__tools">
          <button type="button" class="tb-icon-btn" data-lb-zoom aria-pressed="false" aria-label="Zoom in"></button>
          <button type="button" class="tb-icon-btn" data-lb-close aria-label="Close image viewer">${icon('x', 22)}</button>
        </div>
      </div>
      <div class="tb-lightbox__stage"><img class="tb-lightbox__img" alt="" draggable="false"></div>
      ${images.length > 1 ? `
        <button type="button" class="tb-lightbox__nav tb-lightbox__nav--prev" data-lb-prev aria-label="Previous image">${icon('chevron-left', 26)}</button>
        <button type="button" class="tb-lightbox__nav tb-lightbox__nav--next" data-lb-next aria-label="Next image">${icon('chevron-right', 26)}</button>
        <ul class="tb-lightbox__thumbs" role="list">${images.map((im, i) => `<li><button type="button" class="tb-lightbox__thumb" data-lb-thumb="${i}"><img src="${im.thumb}" alt="" loading="lazy"></button></li>`).join('')}</ul>` : ''}
      <p class="tb-lightbox__hint" aria-hidden="true"></p>`;
    document.body.appendChild(box);
    TB.lock();

    const stage = TB.qs('.tb-lightbox__stage', box);
    const img = TB.qs('.tb-lightbox__img', box);
    const count = TB.qs('.tb-lightbox__count', box);
    const zoomBtn = TB.qs('[data-lb-zoom]', box);
    const hint = TB.qs('.tb-lightbox__hint', box);
    let index = start;
    let zoomed = false;
    let origin = { x: 50, y: 50 };
    let drag = null;

    const paint = () => {
      stage.classList.toggle('is-zoomed', zoomed);
      img.style.transformOrigin = `${origin.x}% ${origin.y}%`;
      img.style.transform = zoomed ? `scale(${ZOOM})` : '';
      zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
      zoomBtn.innerHTML = icon(zoomed ? 'zoom-out' : 'zoom-in', 20);
      hint.textContent = zoomed ? 'Move or drag to look closer · Esc to zoom out' : 'Tap the image to zoom';
    };
    const show = (i) => {
      index = (i + images.length) % images.length;
      zoomed = false;
      const im = images[index];
      img.src = im.src;
      img.alt = im.alt;
      box.setAttribute('aria-label', `${title} — image ${index + 1} of ${images.length}`);
      count.innerHTML = `${index + 1} / ${images.length}<span class="tb-visually-hidden">${im.alt ? `: ${im.alt.replace(/</g, '&lt;')}` : ''}</span>`;
      TB.qsa('[data-lb-thumb]', box).forEach((t, n) => {
        t.classList.toggle('is-active', n === index);
        t.setAttribute('aria-label', `Show image ${n + 1}${images[n].alt ? `: ${images[n].alt}` : ''}`);
        if (n === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      paint();
    };
    const close = () => {
      document.removeEventListener('keydown', onKey, true);
      box.remove();
      box = null;
      TB.unlock();
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
    const point = (e) => {
      const r = stage.getBoundingClientRect();
      return {
        x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)),
        y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)),
      };
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (zoomed) {
          zoomed = false;
          paint();
        } else close();
      } else if (e.key === 'ArrowRight' && images.length > 1) show(index + 1);
      else if (e.key === 'ArrowLeft' && images.length > 1) show(index - 1);
      else if (e.key === '+' || e.key === '=' || e.key.toLowerCase() === 'z') {
        zoomed = !zoomed;
        paint();
      } else if (e.key === '-') {
        zoomed = false;
        paint();
      } else if (e.key === 'Tab') {
        const items = TB.qsa('button', box).filter((b) => b.offsetParent !== null);
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);

    box.addEventListener('click', (e) => {
      if (e.target.closest('[data-lb-close]')) close();
      else if (e.target.closest('[data-lb-prev]')) show(index - 1);
      else if (e.target.closest('[data-lb-next]')) show(index + 1);
      else if (e.target.closest('[data-lb-zoom]')) {
        zoomed = !zoomed;
        paint();
      } else {
        const t = e.target.closest('[data-lb-thumb]');
        if (t) show(Number(t.getAttribute('data-lb-thumb')));
      }
    });
    stage.addEventListener('pointerdown', (e) => {
      drag = { x: e.clientX, y: e.clientY, ox: origin.x, oy: origin.y, moved: false };
      stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', (e) => {
      if (!zoomed) return;
      if (e.pointerType === 'mouse' && !drag) {
        origin = point(e);
        paint();
        return;
      }
      if (!drag) return;
      const r = stage.getBoundingClientRect();
      const dx = ((e.clientX - drag.x) / r.width) * 100;
      const dy = ((e.clientY - drag.y) / r.height) * 100;
      if (Math.abs(dx) + Math.abs(dy) > 1) drag.moved = true;
      origin = { x: Math.min(100, Math.max(0, drag.ox - dx)), y: Math.min(100, Math.max(0, drag.oy - dy)) };
      paint();
    });
    stage.addEventListener('pointerup', (e) => {
      const d = drag;
      drag = null;
      if (d && d.moved) return;
      if (!zoomed) origin = point(e);
      zoomed = !zoomed;
      paint();
    });
    stage.addEventListener('pointercancel', () => (drag = null));

    show(start);
    setTimeout(() => TB.qs('[data-lb-close]', box) && TB.qs('[data-lb-close]', box).focus(), 30);
  }
  TB.openLightbox = openLightbox;
})();

/* Product page extras: Shopify's product recommendations ("You may also like")
   and the complete-the-look plans, which follow the chosen set option. */
(() => {
  const TB = window.TB;

  if (!customElements.get('tb-related')) {
    class TBRelated extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        const load = () => this.load();
        if (!('IntersectionObserver' in window)) return load();
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.io.disconnect();
              load();
            }
          },
          { rootMargin: '0px 0px 600px 0px' },
        );
        this.io.observe(this);
      }
      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }
      async load() {
        const section = this.closest('.shopify-section');
        try {
          const res = await fetch(this.dataset.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const doc = TB.parseHTML(await res.text());
          const next = doc.querySelector('tb-related [data-tb-related-content]');
          if (next && next.children.length) {
            this.innerHTML = '';
            this.appendChild(next);
            this.hidden = false;
            if (section) section.hidden = false;
          } else if (section && !this.hasAttribute('data-keep')) {
            section.hidden = true;
          }
        } catch (e) {
          if (section && !this.hasAttribute('data-keep')) section.hidden = true;
        }
      }
    }
    customElements.define('tb-related', TBRelated);
  }

  /* Complete the look: the section renders a plan for each situation (option
     still to choose, set already has a square, nothing included) and shows
     the one that fits the chosen variant. */
  if (!customElements.get('tb-ctl')) {
    class TBCtl extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.onVariant = (e) => {
          const { variant, complete, product } = e.detail;
          if (product && String(product.id) !== this.dataset.productId) return;
          let plan = 'full';
          if (this.dataset.hasSet === 'true') {
            if (!complete || !variant) plan = 'pending';
            else {
              const c = variant.contents || [];
              if (c.includes('square') && c.includes('cufflinks')) plan = 'none';
              else if (c.includes('square')) plan = 'square';
            }
          }
          this.show(plan);
        };
        document.addEventListener('tb:variant:change', this.onVariant);
      }
      disconnectedCallback() {
        document.removeEventListener('tb:variant:change', this.onVariant);
        this.ready = false;
      }
      show(plan) {
        let any = false;
        TB.qsa('[data-tb-ctl-plan]', this).forEach((el) => {
          const on = el.getAttribute('data-tb-ctl-plan') === plan;
          el.hidden = !on;
          if (on) any = true;
        });
        this.hidden = !any;
      }
    }
    customElements.define('tb-ctl', TBCtl);
  }
})();

/* Product page extras: offer codes copy on tap (the chip shows "Copied" and a
   tick for a moment), and the rating scrolls down to the Judge.me reviews. */
(() => {
  const TB = window.TB;
  if (TB.offersReady) return;
  TB.offersReady = true;

  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      /* falls back below */
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    area.remove();
    return ok;
  };

  // a code reached with the keyboard: bring its whole card into the row's view
  document.addEventListener('focusin', (e) => {
    const card = e.target.closest && e.target.closest('.tb-offer');
    const list = card && card.parentElement;
    if (!list || !list.classList.contains('tb-offers__list')) return;
    const l = list.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    if (r.left >= l.left - 1 && r.right <= l.right + 1) return;
    const pad = parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;
    list.scrollTo({ left: list.scrollLeft + r.left - l.left - pad, behavior: TB.reducedMotion() ? 'auto' : 'smooth' });
  });

  const reviewsTarget = () =>
    document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget, [data-tb-reviews]');

  document.addEventListener('click', async (e) => {
    const chip = e.target.closest('[data-tb-copy]');
    if (chip) {
      const code = chip.getAttribute('data-tb-copy');
      if (!(await copyText(code))) {
        TB.announce(`The code is ${code}.`);
        return;
      }
      chip.classList.add('is-copied');
      TB.announce(`Code ${code} copied.`);
      clearTimeout(chip.tbCopied);
      chip.tbCopied = setTimeout(() => chip.classList.remove('is-copied'), 1800);
      return;
    }

    const rating = e.target.closest('[data-tb-rating]');
    if (rating) {
      e.preventDefault();
      const target = reviewsTarget();
      if (!target) return;
      const scroller = TB.scroller();
      const isPage = scroller === document.scrollingElement || scroller === document.documentElement;
      const header = document.querySelector('.tb-header-section');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const top = target.getBoundingClientRect().top - (isPage ? 0 : scroller.getBoundingClientRect().top) + scroller.scrollTop - offset;
      scroller.scrollTo({ top: Math.max(0, top), behavior: TB.reducedMotion() ? 'auto' : 'smooth' });
    }
  });
})();

/* Collection filters and sort, using Shopify's storefront filtering
   (Search & Discovery app). Every change re-renders the section through the
   Section Rendering API and updates the address, so filtered views stay
   linkable. Desktop shows a menu per filter; phones use the filter sheet. */
(() => {
  const TB = window.TB;
  if (customElements.get('tb-facets')) return;

  const PARTS = ['bar', 'chips', 'results', 'sheet-body', 'sheet-foot'];

  class TBFacets extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sid = this.dataset.sectionId;
      this.controller = null;
      this.onPrice = TB.debounce((form) => this.apply(this.paramsFrom(form)), 700);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('click', (e) => this.onClick(e));

      // the filter sheet is moved to <body>; listen there too
      this.sheet = document.getElementById(`tb-filters-${this.sid}`);
      this.onDocChange = (e) => {
        if (this.sheet && this.sheet.contains(e.target)) this.onChange(e);
      };
      this.onDocInput = (e) => {
        if (this.sheet && this.sheet.contains(e.target)) this.onInput(e);
      };
      this.onDocSubmit = (e) => {
        if (this.sheet && this.sheet.contains(e.target)) this.onSubmit(e);
      };
      this.onDocClick = (e) => {
        if (this.sheet && this.sheet.contains(e.target)) this.onClick(e);
        const open = TB.qs('.tb-facet-menu.is-open', this);
        if (open && !open.contains(e.target)) this.toggleMenu(open, false);
      };
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        const open = TB.qs('.tb-facet-menu.is-open', this);
        if (open) {
          this.toggleMenu(open, false);
          TB.qs('button', open).focus();
        }
      };
      this.onFocusIn = (e) => {
        const open = TB.qs('.tb-facet-menu.is-open', this);
        if (open && !open.contains(e.target) && e.target !== document.body) this.toggleMenu(open, false);
      };
      document.addEventListener('change', this.onDocChange);
      document.addEventListener('input', this.onDocInput);
      document.addEventListener('submit', this.onDocSubmit);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('focusin', this.onFocusIn);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onDocChange);
      document.removeEventListener('input', this.onDocInput);
      document.removeEventListener('submit', this.onDocSubmit);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('focusin', this.onFocusIn);
      this.ready = false;
    }

    /** Filter values from one of the two forms (bar or sheet), plus the sort. */
    paramsFrom(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (key === 'sort_by' || String(value).trim() === '') return;
        params.append(key, value);
      });
      const sort = TB.qs('select[name="sort_by"]', this);
      if (sort && sort.value && sort.value !== sort.getAttribute('data-default')) params.set('sort_by', sort.value);
      return params;
    }

    onChange(e) {
      const form = e.target.closest('form[data-tb-facets-form]');
      if (!form) return;
      if (e.target.matches('[data-tb-price-input]')) return; // handled on input / apply
      this.apply(this.paramsFrom(form));
    }
    onInput(e) {
      if (!e.target.matches('[data-tb-price-input]')) return;
      this.onPrice(e.target.closest('form'));
    }
    onSubmit(e) {
      const form = e.target.closest('form[data-tb-facets-form]');
      if (!form) return;
      e.preventDefault();
      this.apply(this.paramsFrom(form));
    }
    onClick(e) {
      const menuBtn = e.target.closest('[data-tb-facet-toggle]');
      if (menuBtn) {
        const menu = menuBtn.closest('.tb-facet-menu');
        const others = TB.qsa('.tb-facet-menu.is-open', this).filter((m) => m !== menu);
        others.forEach((m) => this.toggleMenu(m, false));
        this.toggleMenu(menu, !menu.classList.contains('is-open'));
        return;
      }
      const link = e.target.closest('a[data-tb-facet-link]');
      if (link) {
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.apply(url.searchParams, url.pathname);
        return;
      }
      const more = e.target.closest('[data-tb-load-more]');
      if (more) {
        e.preventDefault();
        this.loadMore(more);
      }
    }

    toggleMenu(menu, open) {
      menu.classList.toggle('is-open', open);
      const btn = TB.qs('[data-tb-facet-toggle]', menu);
      const panel = TB.qs('.tb-facet-menu__panel', menu);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
    }

    async apply(params, pathname = window.location.pathname) {
      const query = params.toString();
      const pageUrl = `${pathname}${query ? `?${query}` : ''}`;
      window.history.replaceState(window.history.state, '', pageUrl);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const results = document.getElementById(`tb-facets-${this.sid}-results`);
      if (results) results.classList.add('is-loading');

      // remember what the shopper was doing so the re-render doesn't lose it
      const openMenu = TB.qs('.tb-facet-menu.is-open', this);
      const openFacet = openMenu ? openMenu.getAttribute('data-facet') : null;
      const activeId = document.activeElement && document.activeElement.id;
      const sheetBody = document.getElementById(`tb-facets-${this.sid}-sheet-body`);
      const sheetScroll = sheetBody ? sheetBody.closest('.tb-drawer__body') : null;
      const scrollTop = sheetScroll ? sheetScroll.scrollTop : 0;
      const openAcc = sheetBody ? TB.qsa('[data-tb-accordion].is-open', sheetBody).map((a) => a.getAttribute('data-facet')) : [];

      try {
        const res = await fetch(`${pageUrl}${query ? '&' : '?'}section_id=${encodeURIComponent(this.sid)}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = TB.parseHTML(await res.text());
        PARTS.forEach((part) => {
          const id = `tb-facets-${this.sid}-${part}`;
          const cur = document.getElementById(id);
          const next = doc.getElementById(id);
          if (cur && next) cur.innerHTML = next.innerHTML;
        });
        // sheet accordions keep their open state
        const body = document.getElementById(`tb-facets-${this.sid}-sheet-body`);
        if (body) {
          TB.qsa('[data-tb-accordion]', body).forEach((acc) => {
            const was = openAcc.includes(acc.getAttribute('data-facet'));
            if (openAcc.length) TB.setAccordion(acc, was || acc.classList.contains('has-active'));
          });
          if (sheetScroll) sheetScroll.scrollTop = scrollTop;
        }
        if (openFacet) {
          const menu = this.querySelector(`.tb-facet-menu[data-facet="${CSS.escape(openFacet)}"]`);
          if (menu) this.toggleMenu(menu, true);
        }
        if (activeId) {
          const el = document.getElementById(activeId);
          if (el) el.focus({ preventScroll: true });
        }
        const count = TB.qs('[data-tb-coll-count]', this);
        if (count) TB.announce(count.textContent.trim());
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = pageUrl;
      } finally {
        const r = document.getElementById(`tb-facets-${this.sid}-results`);
        if (r) r.classList.remove('is-loading');
      }
    }

    async loadMore(btn) {
      const url = btn.getAttribute('href') || btn.getAttribute('data-url');
      if (!url || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('section_id', this.sid);
        const res = await fetch(u.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = TB.parseHTML(await res.text());
        const grid = TB.qs('[data-tb-grid]', this);
        const nextGrid = doc.querySelector('[data-tb-grid]');
        if (grid && nextGrid) {
          const items = Array.from(nextGrid.children);
          items.forEach((li) => grid.appendChild(li));
          const firstLink = items[0] && TB.qs('a[href]', items[0]);
          if (firstLink) firstLink.focus({ preventScroll: true });
        }
        const more = TB.qs('[data-tb-more]', this);
        const nextMore = doc.querySelector('[data-tb-more]');
        if (more) {
          if (nextMore) more.innerHTML = nextMore.innerHTML;
          else more.remove();
        }
        TB.announce('More products loaded.');
      } catch (e) {
        window.location.href = url;
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('tb-facets', TBFacets);
})();

/* Start-up and theme editor support. */
(() => {
  const TB = window.TB;

  const init = (scope = document) => {
    TB.portal(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: sections are re-rendered in place; move their overlays again
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    // overlays that belonged to the removed section live in <body>
    const ids = TB.qsa('[data-tb-portal][id]', e.target).map((el) => el.id);
    if (TB.isOpen && ids.some((id) => TB.isOpen(id))) TB.closePanel({ restoreFocus: false });
  });
  document.addEventListener('shopify:section:select', (e) => {
    TB.qsa('tb-auto-scroller', e.target).forEach((a) => a.m && (a.m.hover = true));
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    TB.qsa('tb-auto-scroller', e.target).forEach((a) => a.m && (a.m.hover = false));
  });
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    // an edit tab: show it
    const tab = block.closest('[role="tab"]');
    const tabs = block.closest('tb-tabs');
    if (tab && tabs) tabs.select(tabs.tabs.indexOf(tab), { animate: false });
    // a hotspot: open it
    const spot = block.closest('.tb-spot');
    const spots = block.closest('tb-hotspots');
    if (spot && spots) spots.open(spot);
    // a header menu feature/card: open its panel
    const header = document.querySelector('tb-header');
    const panel = block.closest('[data-tb-mega-panel]');
    if (header && panel) header.setOpen(panel.id);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const spots = e.target.closest('tb-hotspots');
    if (spots) spots.open(null);
    const header = document.querySelector('tb-header');
    if (header && e.target.closest('[data-tb-mega-panel]')) header.setOpen(null);
  });
})();
